import { useQuery } from '@tanstack/react-query'
import { ModuleHeader, SpinnerWithBackground, TabBar } from '@viapeople/components'
import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useModulePermission } from '../../hooks/useModulePermission'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import { filterTabsByRole } from '../../utils/tabFiltering'
import { trpc } from '../../utils/trpc'
import type { LoopData } from './store'
import { useLoopStore } from './store'

const tabs = [
	{ id: 'general', label: 'General' },
	{ id: 'participants', label: 'Self Evaluators' },
	{ id: 'configuration', label: 'Configuration' },
	{ id: 'schedule', label: 'Schedule' },
	{ id: 'email-template', label: 'Email Template' },
	{ id: 'launch', label: 'Launch' },
	{ id: 'results', label: 'Results' },
]

export const ManageLoopPage = () => {
	const { loopId } = useParams<{ loopId: string }>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId || ''
	const navigate = useNavigate()
	const toast = useToast()
	const { isLoading: isCheckingPermission, isAuthorized, role } = useModulePermission('loops')

	useEffect(() => {
		if (!isCheckingPermission && !isAuthorized) {
			toast.showToast('You do not have permission to access Loops', 'error')
			navigate('/')
		}
	}, [isCheckingPermission, isAuthorized, navigate, toast])

	const { data: organization, isLoading: isLoadingOrg } = useQuery({
		...trpc.client.organizations.getById.queryOptions({ id: organizationId }),
		enabled: !!organizationId,
	})

	const { data: loopData, isLoading: isLoadingLoop } = useQuery({
		...trpc.client.loops.getById.queryOptions({ id: loopId as string, organizationId: organizationId }),
		enabled: !!loopId && loopId !== 'create' && !!organizationId,
	})

	const location = useLocation()
	const pathArray = location.pathname.split('/')
	const currentTab = pathArray?.[3] ?? 'general'

	const { loop, setLoop } = useLoopStore()

	useEffect(() => {
		if (loopData) {
			// Map backend data to LoopData interface
			// Map 'archived' status to 'completed' for frontend compatibility
			const frontendStatus = loopData.status === 'archived' ? 'completed' : (loopData.status as LoopData['status'])

			setLoop({
				id: loopData.id,
				name: loopData.name,
				description: loopData.description,
				status: frontendStatus,
				organizationId: loopData.organizationId,
				textQuestionLabel: loopData.textQuestionLabel,
				ratingQuestionLabel: loopData.ratingQuestionLabel,
				textQuestionEnabled: loopData.textQuestionEnabled,
				ratingQuestionEnabled: loopData.ratingQuestionEnabled,
				ratingScale: loopData.ratingScale,
				scheduleType: (loopData.scheduleType as 'one-time' | 'recurring') || 'one-time',
				scheduleFrequency: (loopData.scheduleFrequency as 'daily' | 'weekly' | 'monthly' | 'quarterly') || null,
				scheduleStartDate: loopData.scheduleStartDate
					? new Date(loopData.scheduleStartDate).toISOString().split('T')[0]
					: null,
				scheduleEndDate: loopData.scheduleEndDate
					? new Date(loopData.scheduleEndDate).toISOString().split('T')[0]
					: null,
				emailTemplateId: loopData.emailTemplateId || null,
				populationId: loopData.populationId || null,
			})
		} else if (organizationId) {
			setLoop({
				id: '',
				name: '',
				description: null,
				status: 'draft',
				organizationId,
				textQuestionLabel: null,
				ratingQuestionLabel: null,
				textQuestionEnabled: true,
				ratingQuestionEnabled: true,
				ratingScale: 5,
				scheduleType: 'one-time',
				scheduleFrequency: null,
				scheduleStartDate: null,
				scheduleEndDate: null,
				emailTemplateId: null,
				populationId: null,
			})
		}
	}, [loopData, organizationId, setLoop])

	const handleTabChange = (tabId: string) => {
		const loopPath = loopId === 'create' || !loopId ? 'create' : loopId
		navigate(`/loops/${loopPath}/${tabId}`)
	}

	const availableTabs = useMemo(() => filterTabsByRole(tabs, loopId, role), [loopId, role])

	if (isCheckingPermission || isLoadingOrg || isLoadingLoop) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	if (!isAuthorized) {
		return null
	}

	return (
		<main className='w-full p-6 min-h-full'>
			<div className='gap-6 flex flex-col'>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Home', to: '/' },
						{ label: 'Loops', to: '/loops' },
						{
							label: loop?.name || (loopId && loopId !== 'create' ? 'Manage Loop' : 'Create Loop'),
						},
					]}
					title={loop?.name || (loopId && loopId !== 'create' ? 'Manage Loop' : 'Create Loop')}
					logo={{
						url: organization?.logo || '',
						name: organization?.name || '',
					}}
				/>
				<TabBar tabs={availableTabs} activeTab={currentTab} onChange={handleTabChange} />
			</div>
			<Outlet context={{ organization, loop }} />
		</main>
	)
}
